import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '@/utils/formatCurrency';
import { format } from 'date-fns';
import { sv, enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

type Order = {
  id: string;
  display_id?: number;
  created_at: string;
  status: string;
  total: number;
  items: {
    product_name: string;
    quantity: number;
    price: number;
  }[];
};

export function Orders() {
  const { user, profile } = useAuth();
  const { t, i18n } = useTranslation();
  const [orders, setOrders] = React.useState<Order[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const navigate = useNavigate();

  React.useEffect(() => {
    async function fetchOrders() {
      if (!user) return;

      try {
        setLoading(true);
        
        // Fetch orders for the current user
        const { data, error } = await supabase
          .from('orders')
          .select(`
            id,
            display_id,
            created_at,
            status,
            total,
            items
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        setOrders(data || []);
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError(t('common.fetchOrdersError'));
      } finally {
        setLoading(false);
      }
    }

    fetchOrders();
  }, [user, t]);

  // Get the appropriate date locale
  const dateLocale = i18n.language === 'sv' ? sv : enUS;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="text-sm text-red-700">{error}</div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('common.noOrders')}</h3>
        <p className="text-gray-500 mb-6">{t('common.startShopping')}</p>
        <button
          onClick={() => navigate('/products')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          {t('common.browseProducts')}
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('common.yourOrders')}</h1>
      
      <div className="space-y-6">
        {orders.map((order) => (
          <div key={order.id} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-4 sm:px-6 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  {t('common.orderNumber')}: {order.display_id ? `#${order.display_id}` : order.id.substring(0, 8)}
                </h3>
                <p className="text-sm text-gray-500">
                  {format(new Date(order.created_at), 'PPP', { locale: dateLocale })}
                </p>
              </div>
              <div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  order.status === 'completed' ? 'bg-green-100 text-green-800' :
                  order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                  order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {t(`common.orderStatus.${order.status}`)}
                </span>
              </div>
            </div>
            
            <div className="px-4 py-4 sm:px-6">
              <div className="divide-y divide-gray-200">
                {order.items.map((item, index) => (
                  <div key={index} className="py-3 flex justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.product_name}</p>
                      <p className="text-sm text-gray-500">{t('common.quantity')}: {item.quantity}</p>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="border-t border-gray-200 bg-gray-50 px-4 py-4 sm:px-6">
              <div className="flex justify-between text-base font-medium text-gray-900">
                <p>{t('common.total')}</p>
                <p>{formatCurrency(order.total)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 